import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Campaign, getCampaignById } from "@/api/campaign";
import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { format } from "date-fns";
import { useToast } from "@/hooks/useToast";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

const StatusBadge = ({ status }: { status: Campaign["status"] }) => {
	const variants = {
		pending: "bg-yellow-100 text-yellow-800",
		active: "bg-green-100 text-green-800",
		completed: "bg-gray-100 text-gray-800",
	};

	return (
		<Badge variant="outline" className={variants[status]}>
			{status.charAt(0).toUpperCase() + status.slice(1)}
		</Badge>
	);
};

export function CampaignView() {
	const [campaign, setCampaign] = useState<Campaign | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const { id } = useParams<{ id: string }>();
	const { toast } = useToast();
	const navigate = useNavigate();
	const { isAuthenticated } = useAuth();

	useEffect(() => {
		if (!isAuthenticated) {
			navigate("/login");
			return;
		}

		const loadCampaign = async () => {
			try {
				setLoading(true);
				setError(null);
				console.log("Loading campaign details for ID:", id);

				if (!id) {
					throw new Error("Campaign ID is required");
				}

				const data = await getCampaignById(id);
				console.log("Campaign View Data:", {
					campaign: data,
					audienceMetrics: data?.audienceTargeting?.audienceMetrics,
					segments: data?.audienceTargeting?.selectedSegments,
					zipCodes: data?.audienceTargeting?.zipCodes,
				});

				if (!data || !data.campaignDetails) {
					throw new Error("Campaign not found or invalid data received");
				}

				setCampaign(data);
			} catch (error: unknown) {
				console.error("Error loading campaign:", error);
				const errorMessage =
					error instanceof Error
						? error.message
						: "Failed to load campaign details";
				setError(errorMessage);
				toast({
					variant: "destructive",
					title: "Error",
					description: errorMessage,
				});
			} finally {
				setLoading(false);
			}
		};

		loadCampaign();
	}, [id, toast, navigate, isAuthenticated]);

	if (loading) {
		return (
			<PageContainer>
				<div className="flex flex-col items-center justify-center py-8 space-y-4">
					<Loader2 className="h-8 w-8 animate-spin" />
					<p>Loading campaign details...</p>
				</div>
			</PageContainer>
		);
	}

	if (error || !campaign || !campaign.campaignDetails) {
		return (
			<PageContainer>
				<div className="flex flex-col items-center justify-center py-8 space-y-4">
					<p className="text-red-500">{error || "Campaign not found"}</p>
					<button
						onClick={() => navigate("/campaigns")}
						className="text-primary hover:underline">
						Return to Campaigns List
					</button>
				</div>
			</PageContainer>
		);
	}

	return (
		<PageContainer>
			<CardHeader className="flex flex-row items-center justify-between">
				<div>
					<CardTitle>Campaign Details</CardTitle>
					<CardDescription>View campaign information</CardDescription>
				</div>
				<StatusBadge status={campaign.status} />
			</CardHeader>

			<CardContent className="space-y-6">
				<Card>
					<CardHeader>
						<CardTitle>Basic Information</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div>
							<h4 className="font-medium">Campaign Name</h4>
							<p>{campaign.campaignDetails.campaignName}</p>
						</div>
						<div>
							<h4 className="font-medium">Dealer Name</h4>
							<p>{campaign.campaignDetails.dealerName}</p>
						</div>
						<div>
							<h4 className="font-medium">Budget</h4>
							<p>${campaign.campaignDetails.budget.toLocaleString()}</p>
						</div>
						<div>
							<h4 className="font-medium">CPM</h4>
							<p>${campaign.campaignDetails.cpm}</p>
						</div>
						<div>
							<h4 className="font-medium">Floor Frequency</h4>
							<p>{campaign.campaignDetails.floorFrequency}</p>
						</div>
						<div>
							<h4 className="font-medium">Duration</h4>
							<p>
								{format(
									new Date(campaign.campaignDetails.startDate),
									"MMM d, yyyy"
								)}{" "}
								-{" "}
								{format(
									new Date(campaign.campaignDetails.endDate),
									"MMM d, yyyy"
								)}
							</p>
						</div>
						<div>
							<h4 className="font-medium">Content Focus</h4>
							<p>{campaign.campaignDetails.contentFocus.join(", ")}</p>
						</div>
						<div>
							<h4 className="font-medium">Day Parting</h4>
							<p>{campaign.campaignDetails.dayParting}</p>
						</div>
						<div>
							<h4 className="font-medium">Impressions</h4>
							<p>
								{campaign.campaignDetails.impressions?.toLocaleString() ||
									"N/A"}
							</p>
						</div>
						<div>
							<h4 className="font-medium">Audience Reach</h4>
							<p>
								{campaign.campaignDetails.audienceReach?.toLocaleString() ||
									"N/A"}
							</p>
						</div>
						<div>
							<h4 className="font-medium">Notes</h4>
							<p>{campaign.campaignDetails.notes || "N/A"}</p>
						</div>
					</CardContent>
				</Card>

				{campaign.audienceTargeting && (
					<Card>
						<CardHeader>
							<CardTitle>Audience Targeting</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div>
								<h4 className="font-medium">Target ZIP Code</h4>
								<p>{campaign.audienceTargeting.zipCode}</p>
							</div>
							<div>
								<h4 className="font-medium">Radius</h4>
								<p>{campaign.audienceTargeting.radius} miles</p>
							</div>
							<div>
								<h4 className="font-medium">Included ZIP Codes</h4>
								<p>{campaign.audienceTargeting.zipCodes.join(", ")}</p>
							</div>
							<div>
								<h4 className="font-medium">Selected Segments</h4>
								<p>{campaign.audienceTargeting.selectedSegments.join(", ")}</p>
							</div>
							{campaign.audienceTargeting.audienceMetrics && (
								<div>
									<h4 className="font-medium">Audience Metrics</h4>
									<div className="mt-2 space-y-1">
										<p>
											Total Audience:{" "}
											{campaign.audienceTargeting.audienceMetrics.totalCount?.toLocaleString()}
										</p>
										<p>
											Emails:{" "}
											{campaign.audienceTargeting.audienceMetrics.emailCount?.toLocaleString()}
										</p>
										<p>
											Phones:{" "}
											{campaign.audienceTargeting.audienceMetrics.phoneCount?.toLocaleString()}
										</p>
										<p>
											Devices:{" "}
											{campaign.audienceTargeting.audienceMetrics.deviceCount?.toLocaleString()}
										</p>
										<p>
											IPs:{" "}
											{campaign.audienceTargeting.audienceMetrics.iPCount?.toLocaleString()}
										</p>
									</div>
								</div>
							)}
						</CardContent>
					</Card>
				)}

				{campaign.creativeFiles && campaign.creativeFiles.files && (
					<Card>
						<CardHeader>
							<CardTitle>Creative Files</CardTitle>
						</CardHeader>
						<CardContent>
							{campaign.creativeFiles.files.map((file, index) => (
								<div key={index} className="mb-4">
									<div className="relative aspect-video rounded-lg overflow-hidden bg-black">
										<video
											src={file.url}
											className="w-full h-full object-contain"
											controls
										/>
									</div>
									<p className="mt-2 text-sm font-medium">{file.name}</p>
								</div>
							))}
						</CardContent>
					</Card>
				)}

				{campaign.contactInfo && (
					<Card>
						<CardHeader>
							<CardTitle>Contact Information</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div>
								<h4 className="font-medium">Agency Name</h4>
								<p>{campaign.contactInfo.agencyName}</p>
							</div>
							<div>
								<h4 className="font-medium">Contact Name</h4>
								<p>{campaign.contactInfo.contactName}</p>
							</div>
							<div>
								<h4 className="font-medium">Contact Email</h4>
								<p>{campaign.contactInfo.contactEmail}</p>
							</div>
							<div>
								<h4 className="font-medium">Contact Phone</h4>
								<p>{campaign.contactInfo.contactPhone}</p>
							</div>
						</CardContent>
					</Card>
				)}
			</CardContent>
		</PageContainer>
	);
}
